import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Clock, Check, X as XIcon } from 'lucide-react';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { BoardInvitation } from '../types';

interface InvitationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  invitations: BoardInvitation[];
  onAcceptInvitation: (invitationId: string) => void;
  onDeclineInvitation: (invitationId: string) => void;
}

const InvitationsModal: React.FC<InvitationsModalProps> = ({
  isOpen,
  onClose,
  invitations,
  onAcceptInvitation,
  onDeclineInvitation,
}) => {
  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'editor':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'viewer':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  // Add escape key functionality
  useEscapeKey(onClose, isOpen);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-white/15 backdrop-blur-xl z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="glass-effect rounded-2xl p-6 w-full max-w-md max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Pending Invitations</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Invitations List */}
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {invitations.length === 0 ? (
                <div className="text-center py-8">
                  <Mail size={48} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No pending invitations</p>
                </div>
              ) : (
                invitations.map((invitation) => (
                  <motion.div
                    key={invitation.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="glass-effect rounded-xl p-4 border border-white/30"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-800 mb-1">
                          {invitation.boardName || 'Board Invitation'}
                        </h3>
                        <p className="text-sm text-gray-600 mb-2">
                          Invited by: {invitation.invitedByEmail || 'Unknown'}
                        </p>
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getRoleColor(invitation.role)}`}>
                            {invitation.role}
                          </span>
                          <div className="flex items-center space-x-1 text-xs text-gray-500">
                            <Clock size={12} />
                            <span>{formatTimeAgo(invitation.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex space-x-2">
                      <button
                        onClick={() => onAcceptInvitation(invitation.id)}
                        className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                      >
                        <Check size={16} />
                        <span className="text-sm font-medium">Accept</span>
                      </button>
                      <button
                        onClick={() => onDeclineInvitation(invitation.id)}
                        className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                      >
                        <XIcon size={16} />
                        <span className="text-sm font-medium">Decline</span>
                      </button>
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-white/30">
              <p className="text-xs text-gray-500 text-center">
                Invitations expire in 7 days. You'll be automatically added to the board when you accept.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default InvitationsModal;
